import { useEffect, useMemo, useState } from 'react';
import { Plus, Star, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  adminList, adminCreate, adminUpdate, adminDelete,
  adminReorder, adminUpsertSingleton,
} from '../../../lib/adminApi';
import { SectionHeader, SaveBar, useSaver, ConfirmDelete, Field, Card } from './_ui';

const RATINGS = [5, 4, 3, 2];
const SOURCES = ['Trustpilot', 'Google', 'Facebook', 'WhatsApp', 'Direct'];

function StarPreview({ n }) {
  return (
    <div className="inline-flex items-center gap-0.5" aria-hidden="true">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i < n ? 'text-[#00B67A] fill-[#00B67A]' : 'text-black/20'}`}
          strokeWidth={0}
        />
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({
    rating: '', totalReviews: '', profileUrl: '', label: '', lastUpdated: '',
  });
  const [loading, setLoading] = useState(true);
  const summarySave = useSaver();

  const refresh = async () => {
    const [list, sum] = await Promise.all([
      adminList('trustpilot-reviews'),
      adminList('trustpilot-summary'),
    ]);
    setReviews(list || []);
    setSummary({
      rating: sum?.rating || '',
      totalReviews: sum?.totalReviews ?? '',
      profileUrl: sum?.profileUrl || '',
      label: sum?.label || 'See our reviews',
      lastUpdated: sum?.lastUpdated || '',
    });
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const addReview = async () => {
    await adminCreate('trustpilot-reviews', {
      name: 'New reviewer',
      title: '',
      body: '',
      rating: 5,
      date: new Date().toISOString().slice(0, 10),
      source: 'Trustpilot',
      verified: true,
      published: true,
      order: reviews.length,
    });
    await refresh();
  };

  const saveSummary = () =>
    summarySave.run(async () => {
      await adminUpsertSingleton('trustpilot-summary', {
        rating: String(summary.rating || ''),
        totalReviews: Number(summary.totalReviews) || 0,
        profileUrl: summary.profileUrl,
        label: summary.label,
        lastUpdated: summary.lastUpdated,
      });
    });

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= reviews.length) return;
    const next = [...reviews];
    [next[i], next[j]] = [next[j], next[i]];
    setReviews(next);
    await adminReorder('trustpilot-reviews', next.map((r) => r.id));
  };

  return (
    <div className="max-w-5xl">
      <SectionHeader
        title="Reviews"
        description="Featured reviews shown on the homepage. Only published reviews with a rating of 2 or above are visible to visitors."
        actions={
          <Button
            onClick={addReview}
            className="bg-brand-sun text-brand-ink hover:brightness-95 rounded-full"
            data-testid="add-review"
          >
            <Plus className="w-4 h-4 mr-1" /> Add review
          </Button>
        }
      />

      <Card className="mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Trustpilot rating" hint="e.g. 4.9">
            <Input
              value={summary.rating}
              onChange={(e) => setSummary({ ...summary, rating: e.target.value })}
              data-testid="summary-rating"
            />
          </Field>
          <Field label="Total review count">
            <Input
              type="number"
              value={summary.totalReviews}
              onChange={(e) => setSummary({ ...summary, totalReviews: e.target.value })}
              data-testid="summary-total"
            />
          </Field>
          <Field label="Trustpilot profile URL" cols={2}>
            <Input
              value={summary.profileUrl}
              onChange={(e) => setSummary({ ...summary, profileUrl: e.target.value })}
              data-testid="summary-url"
            />
          </Field>
          <Field label="Header label" hint='e.g. "See our reviews"'>
            <Input
              value={summary.label}
              onChange={(e) => setSummary({ ...summary, label: e.target.value })}
            />
          </Field>
          <Field label="Last updated" hint="YYYY-MM-DD">
            <Input
              value={summary.lastUpdated}
              onChange={(e) => setSummary({ ...summary, lastUpdated: e.target.value })}
            />
          </Field>
        </div>
        <div className="mt-5">
          <SaveBar
            {...summarySave}
            onSave={saveSummary}
            dataTestId="save-summary"
          />
        </div>
      </Card>

      {loading ? (
        <p className="text-brand-ink/60">Loading reviews...</p>
      ) : reviews.length === 0 ? (
        <Card>
          <p className="text-brand-ink/70">
            No reviews yet. Click <strong>Add review</strong> to create your first one.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((r, i) => (
            <ReviewRow
              key={r.id}
              review={r}
              index={i}
              total={reviews.length}
              onChange={(next) =>
                setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...next } : x)))
              }
              onSave={async (next) => {
                await adminUpdate('trustpilot-reviews', r.id, next);
              }}
              onDelete={async () => {
                await adminDelete('trustpilot-reviews', r.id);
                await refresh();
              }}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewRow({ review, index, total, onChange, onSave, onDelete, onMoveUp, onMoveDown }) {
  const saver = useSaver();
  const local = review;
  const save = () => saver.run(() => onSave(local));

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="flex flex-col shrink-0">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"
            aria-label="Move up"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"
            aria-label="Move down"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-w-0 grid md:grid-cols-2 gap-3">
          <Field label="Reviewer name">
            <Input
              value={local.name || ''}
              onChange={(e) => onChange({ name: e.target.value })}
            />
          </Field>
          <Field label="Star rating">
            <Select
              value={String(local.rating || 5)}
              onValueChange={(v) => onChange({ rating: Number(v) })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RATINGS.map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} star{n > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Short title" cols={2}>
            <Input
              value={local.title || ''}
              onChange={(e) => onChange({ title: e.target.value })}
            />
          </Field>
          <Field label="Review body" cols={2}>
            <Textarea
              rows={3}
              value={local.body || ''}
              onChange={(e) => onChange({ body: e.target.value })}
            />
          </Field>
          <Field label="Review date" hint="YYYY-MM-DD">
            <Input
              value={local.date || ''}
              onChange={(e) => onChange({ date: e.target.value })}
            />
          </Field>
          <Field label="Source">
            <Select
              value={local.source || 'Trustpilot'}
              onValueChange={(v) => onChange({ source: v })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <div className="flex items-center gap-6 md:col-span-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch
                checked={!!local.verified}
                onCheckedChange={(v) => onChange({ verified: v })}
              />
              Verified badge
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch
                checked={local.published !== false}
                onCheckedChange={(v) => onChange({ published: v })}
              />
              Published
            </label>
            <div className="ml-auto"><StarPreview n={local.rating || 5} /></div>
          </div>

          <div className="md:col-span-2 flex items-center justify-between pt-2 border-t border-brand-ink/10 mt-2">
            <ConfirmDelete onConfirm={onDelete} />
            <SaveBar {...saver} onSave={save} dataTestId={`save-review-${index}`} />
          </div>
        </div>
      </div>
    </Card>
  );
}
